import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { fetchScholar, updateScholar } from '../lib/scholars.js';
import ScholarForm from '../components/scholars/ScholarForm.jsx';

export default function ScholarsEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin, isActive } = useAuth();
  const [scholar, setScholar] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // `t` is intentionally NOT in the deps. If it were, anything that flips
  // the i18next ready/loaded state would re-fire the fetch, set a new
  // scholar reference, and reset the ScholarForm's in-progress edits.
  // `t` is closed over and stable enough for the not-found message.
  useEffect(() => {
    let cancelled = false;
    fetchScholar(id)
      .then((row) => !cancelled && (row ? setScholar(row) : setError(t('scholars.form.notFound'))))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!isActive) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <h1 className="cta-title">{t('bibliography.form.notActiveTitle')}</h1>
          <p className="cta-body">{t('bibliography.form.notActiveBody')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }

  if (!scholar) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const canEdit = isAdmin || scholar.profile_id === user.id;
  if (!canEdit) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{t('scholars.form.notYours')}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <div className="section-label">{t('scholars.form.savedLabel')}</div>
          <h1 className="cta-title">{scholar.name}</h1>
          <p className="cta-body">{t('scholars.form.savedBody')}</p>
          <button type="button" className="btn-primary" onClick={() => navigate('/scholars')}>
            {t('scholars.form.backToList')}
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(form) {
    setBusy(true);
    try {
      await updateScholar(scholar.id, form);
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">{t('scholars.form.editLabel')}</div>
        <h1 className="font-serif text-3xl font-semibold text-cream">{scholar.name}</h1>
      </header>

      <ScholarForm
        initialValue={scholar}
        submitLabel={t('scholars.form.save')}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/scholars')}
        busy={busy}
      />
    </div>
  );
}
